import { useEffect, useState } from "react";
import { Moon, Sun } from "@phosphor-icons/react";
import { navItems, profile } from "../data/profile";
import { useScrollTo } from "../lib/smoothScroll";
import { gsap, ScrollTrigger, useGSAP, sel } from "../lib/motion";
import { getTheme, setTheme, Theme } from "../lib/theme";
import MobileMenu from "./MobileMenu";
import s from "./Nav.module.css";

export default function Nav() {
  const scrollTo = useScrollTo();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);
  const [theme, setThemeState] = useState<Theme>(getTheme);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // All scroll-linked state comes from ScrollTrigger: no scroll listeners.
  useGSAP(() => {
    ScrollTrigger.create({
      start: 80,
      end: "max",
      onEnter: () => setScrolled(true),
      onLeaveBack: () => setScrolled(false),
      onRefresh: (self) => setScrolled(self.scroll() >= 80),
    });

    gsap.to(sel(s.progress), {
      scaleX: 1,
      ease: "none",
      scrollTrigger: { trigger: document.documentElement, start: "top top", end: "bottom bottom", scrub: 0.4 },
    });

    gsap.utils.toArray<HTMLElement>("main section[id]").forEach((el) => {
      ScrollTrigger.create({
        trigger: el,
        start: "top 45%",
        end: "bottom 45%",
        onToggle: (self) => self.isActive && setActive(`#${el.id}`),
      });
    });
  });

  const go = (hash: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(hash);
  };

  const toggleTheme = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  };

  return (
    <header className={`${s.nav} ${scrolled || open ? s.scrolled : ""}`}>
      <span className={s.progress} aria-hidden="true" />
      <div className={`container ${s.inner}`}>
        <a href="#top" className={s.brand} onClick={go("#top")}>
          {profile.name}
        </a>

        <nav className={s.links} aria-label="Primary">
          {navItems.map((item) => (
            <a
              key={item.hash}
              href={item.hash}
              className={s.link}
              onClick={go(item.hash)}
              aria-current={active === item.hash ? "location" : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <button
          type="button"
          className={s.iconBtn}
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        >
          {theme === "dark" ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
        </button>

        <a href={`mailto:${profile.email}`} className={`btn btn-primary ${s.cta}`}>
          Email me
        </a>

        <button
          type="button"
          className={s.burger}
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="visually-hidden">{open ? "Close menu" : "Open menu"}</span>
          <span className={`${s.bar} ${open ? s.barTop : ""}`} aria-hidden="true" />
          <span className={`${s.bar} ${open ? s.barBottom : ""}`} aria-hidden="true" />
        </button>
      </div>

      <MobileMenu open={open} onNavigate={go} />
    </header>
  );
}
